'use client'

import { useEffect, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { X, PictureInPicture2, Maximize2, Minimize2, Play, Pause, Search, Volume2, VolumeX } from 'lucide-react'
import Marquee from './Marquee'
import { resolveNative } from '@/lib/native-video-resolve'
import { getVideoProgress, saveVideoProgress } from '@/lib/video-history'
import { isCompactScreen } from '@/lib/screen'

function videoBeacon(msg: string) {
  try { const x = new XMLHttpRequest(); x.open('POST', '/api/tvlog', true); x.send('[video] ' + msg) } catch {}
}

/**
 * Lecteur vidéo GLOBAL à deux étages (BLC Video Player).
 *
 *  1er appui « Mini-lecteur » (page watch)  → mode `docked` : petit lecteur en
 *     bas (aperçu + play/pause), continue en naviguant.
 *  Clic sur la vignette / « Détacher »       → mode `float` : une VIGNETTE PiP
 *     PROPRE — la vidéo avec les commandes DESSUS (play/pause, progression,
 *     agrandir, fermer), qu'on DÉPLACE en attrapant la vidéo et qu'on
 *     REDIMENSIONNE par le coin (ratio 16:9 conservé).
 *
 * L'iframe est UNIQUE et persistante : on ne fait que la repositionner en CSS
 * entre les étages → aucun rechargement, la lecture ne saute pas.
 */

type Mode = 'off' | 'inline' | 'docked' | 'float'
type Vid = { videoId: string; title: string; channel: string }

// ---- Store singleton ----
// frTrack : URL d'une PISTE AUDIO française à jouer PAR-DESSUS la vidéo (muette).
// C'est le doublage Voxtral (le serveur ne peut pas produire de mp4 vidéo).
let S: { mode: Mode; vid: Vid | null; frTrack: string | null } = { mode: 'off', vid: null, frTrack: null }
const subs = new Set<() => void>()
const emit = () => subs.forEach(fn => fn())

export function getVideoDockMode(): Mode { return S.mode }
export function setInlineVideo(v: Vid) { S = { mode: 'inline', vid: v, frTrack: null }; emit() }
export function updateVideoMeta(v: Partial<Vid>) { if (S.vid) { S = { ...S, vid: { ...S.vid, ...v } }; emit() } }
export function inlineVideo() { if (S.vid) { S = { ...S, mode: 'inline' }; emit() } }
export function dockVideo() { if (S.vid) { S = { ...S, mode: 'docked' }; emit() } }
export function floatVideo() { if (S.vid) { S = { ...S, mode: 'float' }; emit() } }
export function closeVideoDock() { S = { mode: 'off', vid: null, frTrack: null }; emit() }
/** Active/désactive la voix française par-dessus la vidéo (null = original). */
export function setFrOverlay(url: string | null) { S = { ...S, frTrack: url }; emit() }

// ---- Contrôle EXTERNE (page watch en mode TV, télécommande) ----
// La page watch (TV) n'a AUCUN bouton lecture/pause focusable au D-pad : le
// <video> plein écran n'expose que ses contrôles natifs (inutilisables à la
// télécommande). VideoDock enregistre ici ses commandes → une barre focusable
// (page watch) ou la télécommande peut mettre en pause/lecture et sauter.
let extCtl: { toggle: () => void; seekBy: (d: number) => void; playing: () => boolean; toggleMute: () => void; muted: () => boolean } | null = null
export function toggleVideoDockPlay() { extCtl?.toggle() }
export function seekVideoDockBy(d: number) { extCtl?.seekBy(d) }
export function isVideoDockPlaying() { return !!extCtl?.playing() }
/** Coupe/rétablit UNIQUEMENT le son de la vidéo (pas le reste de l'appli). */
export function toggleVideoDockMute() { extCtl?.toggleMute() }
export function isVideoDockMuted() { return !!extCtl?.muted() }

export function useVideoDockState() {
  const [, force] = useReducer(x => x + 1, 0)
  useEffect(() => { subs.add(force); return () => { subs.delete(force) } }, [])
  return S
}

function loadYT(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve()
    const w = window as any
    if (w.YT && w.YT.Player) return resolve()
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      document.head.appendChild(tag)
    }
    const started = Date.now()
    const iv = setInterval(() => {
      if (w.YT && w.YT.Player) { clearInterval(iv); resolve() }
      else if (Date.now() - started > 15000) { clearInterval(iv); resolve() }
    }, 120)
  })
}

const BAR = 38 // hauteur de la barre de commandes de la vignette flottante
const FLOAT_KEY = 'blc.videofloat.v3'   // v3 : nouvelle position par défaut (petite, en bas à droite)
type Box = { x: number; y: number; w: number; h: number }
function boxFromW(w: number, x: number, y: number): Box { return { x, y, w, h: Math.round((w * 9) / 16) + BAR } }
// Garde la fenêtre DANS l'écran (largeur ≤ écran) — surtout sur mobile.
function clampBox(b: Box): Box {
  if (typeof window === 'undefined') return b
  const w = Math.min(b.w, window.innerWidth - 12)
  const h = Math.round((w * 9) / 16) + BAR
  const x = Math.max(6, Math.min(window.innerWidth - w - 6, b.x))
  const y = Math.max(6, Math.min(window.innerHeight - h - 6, b.y))
  return { x, y, w, h }
}
function defaultBox(): Box {
  if (typeof window === 'undefined') return { x: 20, y: 80, w: 440, h: 285 }
  // Mobile : la fenêtre fait ~la largeur du téléphone/tablette. Desktop : 440px.
  // PETITE fenêtre EN BAS À DROITE, au-dessus des onglets / de la barre de lecture.
  const mobile = isCompactScreen()
  const w = mobile ? Math.min(Math.round(window.innerWidth * 0.64), 300) : 400
  const b = boxFromW(w, Math.max(8, window.innerWidth - w - (mobile ? 10 : 24)), 0)
  return clampBox({ ...b, y: Math.max(12, window.innerHeight - b.h - (mobile ? 170 : 130)) })
}
function loadBox(): Box {
  if (typeof window === 'undefined') return defaultBox()
  try { const raw = localStorage.getItem(FLOAT_KEY); if (raw) { const b = JSON.parse(raw); if (b && typeof b.x === 'number') return clampBox(b) } } catch {}
  return defaultBox()
}

type Rect = { left: number; top: number; width: number; height: number; radius: number }
const sameRect = (a: Rect | null, b: Rect | null) =>
  (!a && !b) || (!!a && !!b && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height)

function fmt(s: number) {
  if (!isFinite(s) || s < 0) s = 0
  const m = Math.floor(s / 60), ss = Math.floor(s % 60)
  return `${m}:${ss < 10 ? '0' : ''}${ss}`
}

export default function VideoDock() {
  const st = useVideoDockState()
  const router = useRouter()

  const hostRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<any>(null)
  const resumedFor = useRef<string | null>(null)   // vidéo déjà « reprise » à sa position enregistrée
  const frAudioRef = useRef<HTMLAudioElement | null>(null)
  const curIdRef = useRef<string | null>(null)
  const [ready, setReady] = useState(false)

  // --- Repli VIDÉO pour la TV : l'embed YouTube est refusé sur les box TV →
  //     on lit un flux MP4 progressif (RapidAPI, /api/video/mp4url) dans un
  //     <video>. Uniquement en mode TV : desktop/mobile gardent l'iframe YT. ---
  const videoElRef = useRef<HTMLVideoElement | null>(null)
  const [tv, setTv] = useState(false)
  useEffect(() => {
    const upd = () => setTv(document.documentElement.classList.contains('tv'))
    upd()
    const t = setTimeout(upd, 900)   // le cookie natif TV peut arriver après le montage
    return () => clearTimeout(t)
  }, [])
  // Grand lecteur TV (baffle) ouvert PAR-DESSUS l'appli vidéo → on CACHE la
  // vidéo (sinon elle transparaît derrière le lecteur, qui doit être opaque).
  const [tvPlayerOpen, setTvPlayerOpen] = useState(false)
  useEffect(() => {
    const upd = () => setTvPlayerOpen(document.documentElement.dataset.tvPlayerOpen === '1')
    upd()
    const obs = new MutationObserver(upd)
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-tv-player-open'] })
    return () => obs.disconnect()
  }, [])
  const useMp4 = tv
  const [mp4Url, setMp4Url] = useState<string | null>(null)
  const mp4CacheRef = useRef<Record<string, string>>({})

  const [rect, setRect] = useState<Rect | null>(null)
  const [box, setBox] = useState<Box>(loadBox)
  const boxRef = useRef<Box>(box)
  const setBox2 = (b: Box) => { boxRef.current = b; setBox(b); try { localStorage.setItem(FLOAT_KEY, JSON.stringify(b)) } catch {} }

  // État de lecture (pour NOS commandes sur la vignette).
  const [pb, setPb] = useState({ playing: false, pos: 0, dur: 0, muted: false })

  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const f = () => setIsMobile(isCompactScreen())
    f(); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f)
  }, [])

  const active = st.mode !== 'off'

  // --- Création paresseuse du lecteur YT (PAS en mode TV : on lit le MP4) ---
  //     Vérif DOM directe (html.tv) : l'état `tv` est async → sans ça le lecteur
  //     YT pouvait se créer au 1er rendu avant que le repli MP4 ne s'active.
  useEffect(() => {
    if (!active || playerRef.current || document.documentElement.classList.contains('tv')) return
    let killed = false
    loadYT().then(() => {
      if (killed || !hostRef.current || playerRef.current) return
      const w = window as any
      playerRef.current = new w.YT.Player(hostRef.current, {
        host: 'https://www.youtube.com',
        width: '100%', height: '100%',
        videoId: S.vid?.videoId || '',
        playerVars: {
          autoplay: 1, rel: 0, modestbranding: 1, playsinline: 1,
          controls: 1, fs: 1, enablejsapi: 1,
          ...(typeof window !== 'undefined' ? { origin: window.location.origin } : {}),
        },
        events: {
          onReady: () => {
            setReady(true)
            curIdRef.current = S.vid?.videoId || null
            try {
              const f = playerRef.current?.getIframe?.()
              if (f) f.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture')
            } catch {}
          },
        },
      })
    })
    return () => { killed = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  // --- Charge la vidéo courante quand elle change (moteur YT) ---
  useEffect(() => {
    if (!ready || !playerRef.current || !st.vid) return
    if (curIdRef.current === st.vid.videoId) return
    try { playerRef.current.loadVideoById(st.vid.videoId); curIdRef.current = st.vid.videoId } catch {}
  }, [ready, st.vid?.videoId])

  // --- Moteur MP4 (TV) : résout l'URL MP4 de la vidéo courante et la joue ---
  useEffect(() => {
    if (!useMp4 || !active || !st.vid) return
    const id = st.vid.videoId
    let dead = false
    const apply = (url: string) => {
      if (dead) return
      mp4CacheRef.current[id] = url
      curIdRef.current = id
      setMp4Url(url)
      setReady(true)
    }
    const cached = mp4CacheRef.current[id]
    if (cached) { apply(cached); return }
    setMp4Url(null)   // évite d'afficher l'ancienne vidéo pendant la résolution
    // RÉSOLUTION NATIVE EN PRIORITÉ (NewPipeExtractor, EN LOCAL, sa propre IP —
    // comme SmartTube/NewPipe) : dispo uniquement dans l'APK, renvoie null sur
    // le web → repli automatique sur l'API serveur, comportement inchangé.
    videoBeacon('resolve start vid=' + id)
    // Timeout généreux (25 s, pas les 12 s par défaut) : le repli serveur
    // (RapidAPI) est mort (quota mensuel épuisé) → aucun intérêt à abandonner
    // tôt sur un appareil lent (box TV), autant laisser NewPipeExtractor finir.
    resolveNative(id, 25000, diag => {
      videoBeacon('native diag vid=' + id + ' phase=' + diag.phase + ' elapsedMs=' + diag.elapsedMs + (diag.error ? ' error=' + diag.error : ''))
    }).then(native => {
      if (dead) return
      if (native?.videoUrl) {
        videoBeacon('native OK vid=' + id)
        apply(native.videoUrl)
        if (native.title && !S.vid?.title) updateVideoMeta({ title: native.title })
        return
      }
      videoBeacon('native pas d URL vid=' + id + ' -> repli serveur')
      fetch(`/api/video/mp4url?id=${id}`, { cache: 'no-store' })
        .then(r => r.json())
        .then(j => {
          videoBeacon('mp4url vid=' + id + ' -> ' + (j?.ok ? 'OK' : 'ECHEC ' + (j?.error || '')))
          if (j?.ok && j.url) apply(j.url)
          // mp4url renvoie aussi le titre → on remplit le mini-lecteur quand le
          // fetch de détails de la page watch a échoué (fréquent sur box TV).
          if (j?.title && !S.vid?.title) updateVideoMeta({ title: j.title })
        })
        .catch(() => { videoBeacon('mp4url vid=' + id + ' -> fetch throw') })
    })
    return () => { dead = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMp4, active, st.vid?.videoId])

  // Relance la lecture quand la source MP4 change (autoPlay ne rejoue pas seul).
  useEffect(() => {
    if (!useMp4 || !mp4Url) return
    const v = videoElRef.current
    if (v) { try { v.play().catch(() => {}) } catch {} }
  }, [useMp4, mp4Url])

  // --- Stoppe la lecture quand on ferme ---
  useEffect(() => {
    if (st.mode === 'off') {
      if (useMp4) { const v = videoElRef.current; if (v) { try { v.pause() } catch {} } }
      else { try { playerRef.current?.stopVideo?.() } catch {} }
      curIdRef.current = null
    }
  }, [st.mode, useMp4])

  // --- Sonde l'état de lecture pour NOS commandes (inline + docked + float) ---
  // ⚠️ Doit tourner AUSSI en INLINE (page watch, TV) : sinon `pb.playing` reste
  // figé → le bouton Lecture/Pause ne se transforme jamais en Pause (bug signalé),
  // et isVideoDockPlaying() renvoie faux à la barre de la page watch.
  useEffect(() => {
    if (st.mode === 'off') return
    // Progression (« Reprendre la lecture ») : reprise à la position enregistrée
    // au 1er tick où la durée est connue, puis sauvegarde toutes les ~5 s.
    let lastSave = 0
    const track = (vid: string | undefined, pos: number, dur: number, seek: (t: number) => void) => {
      if (!vid || !(dur > 0)) return
      if (resumedFor.current !== vid) {
        resumedFor.current = vid
        const saved = getVideoProgress(vid)
        if (saved && saved.pos > 10 && saved.pos < dur - 20 && pos < 5) { try { seek(saved.pos) } catch {} }
        return
      }
      const now = Date.now()
      if (now - lastSave > 5000) { lastSave = now; saveVideoProgress(vid, pos, dur) }
    }
    const iv = setInterval(() => {
      if (useMp4) {
        const v = videoElRef.current
        if (!v) return
        setPb({ playing: !v.paused && !v.ended, pos: v.currentTime || 0, dur: v.duration || 0, muted: v.muted })
        track(st.vid?.videoId, v.currentTime || 0, v.duration || 0, t => { v.currentTime = t })
        return
      }
      const p = playerRef.current
      if (!p || typeof p.getCurrentTime !== 'function') return
      try {
        const stt = p.getPlayerState?.()
        setPb({ playing: stt === 1 || stt === 3, pos: p.getCurrentTime() || 0, dur: p.getDuration?.() || 0, muted: !!p.isMuted?.() })
        if (stt === 1) track(st.vid?.videoId, p.getCurrentTime() || 0, p.getDuration?.() || 0, t => p.seekTo(t, true))
      } catch {}
    }, 400)
    return () => clearInterval(iv)
  }, [st.mode, useMp4, st.vid?.videoId])

  // --- Overlay VOIX FR : coupe le son de la vidéo + joue la piste FR synchro ---
  useEffect(() => {
    if (!st.frTrack) {
      if (useMp4) { const v = videoElRef.current; if (v) v.muted = false }
      else { try { playerRef.current?.unMute?.() } catch {} }
      const a = frAudioRef.current; if (a) { try { a.pause() } catch {} }
      return
    }
    const a0 = frAudioRef.current
    try { a0?.play?.().catch(() => {}) } catch {}   // tente de démarrer dans le geste
    const iv = setInterval(() => {
      const a = frAudioRef.current; if (!a) return
      try {
        if (useMp4) {
          const v = videoElRef.current; if (!v) return
          v.muted = true
          const t = v.currentTime || 0
          if (Math.abs((a.currentTime || 0) - t) > 0.35) a.currentTime = t
          if (!v.paused && !v.ended) { if (a.paused) a.play().catch(() => {}) }
          else if (!a.paused) a.pause()
        } else {
          const p = playerRef.current
          if (!p || typeof p.getCurrentTime !== 'function') return
          p.mute?.()
          const t = p.getCurrentTime() || 0
          if (Math.abs((a.currentTime || 0) - t) > 0.35) a.currentTime = t
          const stt = p.getPlayerState?.()
          if (stt === 1) { if (a.paused) a.play().catch(() => {}) }
          else if (!a.paused) a.pause()
        }
      } catch {}
    }, 300)
    return () => clearInterval(iv)
  }, [st.frTrack, ready, useMp4])

  const togglePlay = () => {
    if (useMp4) { const v = videoElRef.current; if (!v) return; try { v.paused ? v.play() : v.pause() } catch {}; return }
    const p = playerRef.current; if (!p) return; try { pb.playing ? p.pauseVideo() : p.playVideo() } catch {}
  }
  const seekTo = (v: number) => {
    if (useMp4) { const el = videoElRef.current; if (el) { try { el.currentTime = v } catch {} } setPb(s => ({ ...s, pos: v })); return }
    const p = playerRef.current; if (!p) return; try { p.seekTo(v, true) } catch {}; setPb(s => ({ ...s, pos: v }))
  }
  // Coupe/rétablit UNIQUEMENT le son de CETTE vidéo (pas le volume système, pas
  // le reste de l'appli) — sans effet si la voix FR est active (elle force déjà
  // la vidéo en muet en continu, cf. effet plus haut).
  // Vitesse de lecture (0,75 → 1 → 1,25 → 1,5 → 2).
  const RATES = [0.75, 1, 1.25, 1.5, 2]
  const [rate, setRate] = useState(1)
  const cycleRate = () => {
    const r = RATES[(RATES.indexOf(rate) + 1) % RATES.length]
    setRate(r)
    if (useMp4) { const v = videoElRef.current; if (v) v.playbackRate = r; return }
    try { playerRef.current?.setPlaybackRate?.(r) } catch {}
  }
  const toggleMute = () => {
    if (st.frTrack) return
    if (useMp4) { const v = videoElRef.current; if (!v) return; v.muted = !v.muted; setPb(s => ({ ...s, muted: v.muted })); return }
    const p = playerRef.current; if (!p) return
    try { if (p.isMuted?.()) { p.unMute?.(); setPb(s => ({ ...s, muted: false })) } else { p.mute?.(); setPb(s => ({ ...s, muted: true })) } } catch {}
  }

  // Enregistre les commandes externes (barre focusable page watch + télécommande
  // 'playpause'). Sans tableau de deps → toujours les closures FRAÎCHES (pb à jour).
  useEffect(() => {
    extCtl = { toggle: togglePlay, seekBy: (d) => seekTo(Math.max(0, (pb.pos || 0) + d)), playing: () => pb.playing, toggleMute, muted: () => pb.muted }
    const onToggle = () => togglePlay()
    window.addEventListener('blc-video-toggle', onToggle)
    return () => { window.removeEventListener('blc-video-toggle', onToggle); if (extCtl && extCtl.toggle === togglePlay) extCtl = null }
  })

  // --- Boucle de positionnement (rAF) : place l'iframe selon le mode ---
  useEffect(() => {
    if (!active) { setRect(null); return }
    let raf = 0
    // Écran TV pivoté en CSS (data-tvrot) : getBoundingClientRect donne des
    // coords VISUELLES, mais l'élément vidéo (fixed) vit dans le body TRANSFORMÉ
    // → il faut convertir (comme YoutubePlayer), sinon la vidéo atterrit HORS
    // ÉCRAN → « son mais pas d'image ».
    const conv = (b: DOMRect, radius: number): Rect => {
      let left = b.left, top = b.top, width = b.width, height = b.height
      const tvrot = document.documentElement.dataset.tvrot
      if (tvrot === '90') { const VW = window.innerWidth; left = b.top; top = VW - (b.left + b.width); width = b.height; height = b.width }
      else if (tvrot === '270') { const VH = window.innerHeight; left = VH - (b.top + b.height); top = b.left; width = b.height; height = b.width }
      return { left, top, width, height, radius }
    }
    const tick = () => {
      let r: Rect | null = null
      if (st.mode === 'inline') {
        const el = document.querySelector('[data-video-slot="1"]') as HTMLElement | null
        if (el) r = conv(el.getBoundingClientRect(), 16)
      } else if (st.mode === 'docked') {
        const el = document.getElementById('video-dock-slot')
        if (el) r = conv(el.getBoundingClientRect(), 10)
      } else if (st.mode === 'float') {
        const b = boxRef.current
        r = { left: b.x, top: b.y, width: b.w, height: b.h - BAR, radius: 12 }
      }
      setRect(prev => (sameRect(prev, r) ? prev : r))
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [active, st.mode])

  // --- Auto-dock si inline mais slot disparu (navigation) ---
  useEffect(() => {
    if (st.mode !== 'inline') return
    const t = setInterval(() => {
      // On quitte la page de la vidéo → MINI-FENÊTRE flottante (déplaçable, la
      // lecture continue) ; en TV on garde la barre du haut (focus télécommande).
      if (!document.querySelector('[data-video-slot="1"]')) { if (document.documentElement.classList.contains('tv')) dockVideo(); else floatVideo() }
    }, 900)
    return () => clearInterval(t)
  }, [st.mode])

  // ====== Déplacement (on attrape la VIDÉO) ======
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null)
  const onDragDown = (e: React.PointerEvent) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: boxRef.current.x, oy: boxRef.current.y, moved: false }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onDragMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    if (!dragRef.current.moved && Math.hypot(e.clientX - dragRef.current.sx, e.clientY - dragRef.current.sy) < 6) return
    dragRef.current.moved = true
    const b = boxRef.current
    const nx = Math.max(4, Math.min(window.innerWidth - b.w - 4, dragRef.current.ox + (e.clientX - dragRef.current.sx)))
    const ny = Math.max(4, Math.min(window.innerHeight - b.h - 4, dragRef.current.oy + (e.clientY - dragRef.current.sy)))
    setBox2({ ...b, x: nx, y: ny })
  }
  const onDragUp = (e: React.PointerEvent) => {
    const d = dragRef.current
    dragRef.current = null
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch {}
    // TOUCHER sans glisser = revenir à la vidéo (page de la vidéo, en haut).
    if (d && !d.moved && e.type === 'pointerup' && S.vid) {
      const here = window.location.pathname === `/app/video/watch/${S.vid.videoId}`
      if (here) { window.scrollTo({ top: 0, behavior: 'smooth' }); inlineVideo() }
      else router.push(`/app/video/watch/${S.vid.videoId}`)
    }
  }

  // ====== Redimensionnement (coin bas-droit, ratio 16:9) ======
  const rzRef = useRef<{ sx: number; ow: number } | null>(null)
  const onRzDown = (e: React.PointerEvent) => {
    e.stopPropagation()
    rzRef.current = { sx: e.clientX, ow: boxRef.current.w }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onRzMove = (e: React.PointerEvent) => {
    if (!rzRef.current) return
    const b = boxRef.current
    let nw = Math.max(240, rzRef.current.ow + (e.clientX - rzRef.current.sx))
    nw = Math.min(nw, window.innerWidth - b.x - 4, 900)
    setBox2(boxFromW(nw, b.x, b.y))
  }
  const onRzUp = (e: React.PointerEvent) => { rzRef.current = null; try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch {} }

  // ====== « Image dans l'image » du SYSTÈME (APK Android) ======
  // L'APK (window.AndroidPip) passe l'appli en petite fenêtre sur l'écran d'accueil
  // quand on la quitte pendant qu'une vidéo joue. On lui signale donc si une vidéo
  // est en cours, et il nous prévient (window.__blcSetPip) quand la petite fenêtre
  // s'ouvre/se ferme : on affiche alors UNIQUEMENT la vidéo, plein cadre.
  const [osPip, setOsPip] = useState(false)
  useEffect(() => {
    const w = window as any
    w.__blcSetPip = (on: boolean) => {
      setOsPip(!!on)
      if (on) document.documentElement.setAttribute('data-ospip', '1')
      else document.documentElement.removeAttribute('data-ospip')
    }
    return () => { if (w.__blcSetPip) delete w.__blcSetPip }
  }, [])
  useEffect(() => {
    try { (window as any).AndroidPip?.setVideoActive?.(active && pb.playing) } catch {}
  }, [active, pb.playing])

  // ====== Rendu ======
  // L'iframe capte le pointeur SEULEMENT en inline (contrôles YouTube natifs sur
  // la page watch). En docked/float, pointer-events:none → ce sont NOS overlays
  // (déplacement + commandes) qui reçoivent les clics.
  const visible = active && !!rect && !tvPlayerOpen
  const hostStyle: React.CSSProperties = active && osPip
    ? { position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', zIndex: 2147483000, borderRadius: 0, overflow: 'hidden', background: '#000', pointerEvents: 'none' }
    : visible
    ? {
        position: 'fixed', left: rect!.left, top: rect!.top, width: rect!.width, height: rect!.height,
        zIndex: st.mode === 'inline' ? 30 : 1400, borderRadius: rect!.radius, overflow: 'hidden',
        background: '#000', pointerEvents: st.mode === 'inline' ? 'auto' : 'none',
      }
    : { position: 'fixed', left: -99999, top: -99999, width: 320, height: 180, opacity: 0, pointerEvents: 'none' }

  const dockBottom = isMobile ? 128 : 20
  // Mode TV : mini-lecteur EN HAUT, GRAND, focusable au D-pad (clic = revenir au
  // grand lecteur). Ailleurs : petit, en bas (inchangé).
  const thumbW = tv ? 300 : 150
  const thumbH = tv ? 169 : 84
  const openFull = () => { if (S.vid) router.push(`/app/video/watch/${S.vid.videoId}`) }
  const pct = pb.dur > 0 ? (pb.pos / pb.dur) * 100 : 0
  const seekBg = `linear-gradient(90deg,rgb(var(--a1)) 0%,rgb(var(--a3)) ${pct}%,rgba(255,255,255,0.25) ${pct}%,rgba(255,255,255,0.25) 100%)`

  return (
    <>
      {/* Lecteur vidéo persistant — repositionné, jamais reparenté.
          TV : <video> MP4 (embed YouTube refusé sur box TV). Ailleurs : iframe YT. */}
      <div style={hostStyle}>
        {useMp4
          ? <video ref={videoElRef} src={mp4Url || undefined}
              // controls natifs COUPÉS sur TV : injoignables au D-pad — remplacés
              // par nos boutons focusables (pause sur l'image, ±10 s, Agrandir).
              autoPlay playsInline controls={st.mode === 'inline' && !tv}
              onLoadedMetadata={() => { const v = videoElRef.current; if (v) try { v.play().catch(() => {}) } catch {} }}
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
          : <div ref={hostRef} style={{ width: '100%', height: '100%' }} />}
      </div>

      {/* Piste voix française (doublage Voxtral) jouée par-dessus la vidéo muette */}
      <audio ref={frAudioRef} src={st.frTrack || undefined} preload="auto" style={{ display: 'none' }} />

      {/* ===== Étage 1 : mini-lecteur en bas ===== */}
      {active && st.mode === 'docked' && typeof document !== 'undefined' && createPortal(
        <div
          className={`fixed flex items-center border ${tv ? 'gap-5 rounded-3xl px-5 py-4' : 'left-3 gap-3 rounded-2xl px-3 py-2.5'}`}
          style={{
            ...(tv ? { top: 14, left: '50%', transform: 'translateX(-50%)' } : { bottom: dockBottom }),
            zIndex: 1399,
            background: 'rgba(15,15,20,0.97)', borderColor: 'rgba(255,255,255,0.14)',
            backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
            boxShadow: '0 10px 34px rgba(0,0,0,0.55)',
            maxWidth: 'calc(100vw - 24px)',
          }}
        >
          {/* Aperçu vidéo (live) — TV : clic = REVENIR au grand lecteur ;
              ailleurs : ouvrir la vignette PiP flottante. */}
          <button onClick={tv ? openFull : floatVideo} title={tv ? 'Revenir au lecteur' : 'Ouvrir en vignette flottante'}
            className="blc-tv-float relative shrink-0 group rounded-2xl focus:outline-none focus:ring-4 focus:ring-white/80" style={{ width: thumbW, height: thumbH }}>
            <div id="video-dock-slot" style={{ width: thumbW, height: thumbH, borderRadius: tv ? 16 : 10, background: '#000' }} />
            <span className={`absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 group-hover:opacity-100 transition ${tv ? 'rounded-2xl' : 'rounded-[10px]'}`}>
              {tv ? <Maximize2 size={34} className="text-white" /> : <PictureInPicture2 size={22} className="text-white" />}
            </span>
          </button>
          {/* Play/pause — icône Pause quand ça joue, Play quand en pause. */}
          <button onClick={togglePlay} title={pb.playing ? 'Pause' : 'Lecture'}
            className={`blc-tv-float rounded-full bg-gradient-to-br from-a1 to-a3 text-white flex items-center justify-center shrink-0 active:scale-95 transition focus:outline-none focus:ring-4 focus:ring-white/80 ${tv ? 'w-16 h-16' : 'w-11 h-11'}`}>
            {pb.playing ? <Pause size={tv ? 30 : 20} className="fill-white" /> : <Play size={tv ? 30 : 20} className="ml-0.5 fill-white" />}
          </button>
          <div style={{ minWidth: 0, width: tv ? 360 : 150 }}>
            <div className={`font-semibold text-white ${tv ? 'text-2xl' : 'text-[13px]'}`}><Marquee>{S.vid?.title || 'Vidéo'}</Marquee></div>
            <div className={`text-white/55 truncate ${tv ? 'text-lg mt-1' : 'text-[11px] mt-0.5'}`}>{S.vid?.channel || ''}</div>
          </div>
          {/* PiP flottant : souris uniquement → masqué sur TV. */}
          {!tv && (
            <button onClick={floatVideo} title="Détacher en vignette"
              className="w-9 h-9 rounded-full bg-white/8 hover:bg-white/16 text-white flex items-center justify-center transition shrink-0">
              <PictureInPicture2 size={17} />
            </button>
          )}
          <button onClick={openFull} title="Rouvrir en grand"
            className={`blc-tv-float rounded-full bg-white/8 hover:bg-white/16 text-white flex items-center justify-center transition shrink-0 focus:outline-none focus:ring-4 focus:ring-white/80 ${tv ? 'w-14 h-14' : 'w-9 h-9'}`}>
            <Maximize2 size={tv ? 24 : 16} />
          </button>
          <button onClick={() => router.push('/app/video')} title="Chercher une autre vidéo"
            className={`blc-tv-float rounded-full bg-white/8 hover:bg-white/16 text-white flex items-center justify-center transition shrink-0 focus:outline-none focus:ring-4 focus:ring-white/80 ${tv ? 'w-14 h-14' : 'w-9 h-9'}`}>
            <Search size={tv ? 24 : 16} />
          </button>
          <button onClick={closeVideoDock} title="Fermer"
            className={`blc-tv-float rounded-full bg-white/8 hover:bg-white/16 text-white/70 flex items-center justify-center transition shrink-0 focus:outline-none focus:ring-4 focus:ring-white/80 ${tv ? 'w-14 h-14' : 'w-9 h-9'}`}>
            <X size={tv ? 24 : 16} />
          </button>
        </div>,
        document.body,
      )}

      {/* ===== Étage 2 : VIGNETTE PiP flottante propre (commandes dessus) ===== */}
      {active && st.mode === 'float' && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed select-none"
          style={{
            // ⚠️ PAS de fond opaque : l'iframe vidéo est DERRIÈRE (z 1400) et doit
            // rester visible à travers la zone transparente. Un background:#000
            // ici cachait la vidéo (« pas d'image » dans le PiP).
            left: box.x, top: box.y, width: box.w, height: box.h, zIndex: 1401,
            borderRadius: 14, overflow: 'hidden', background: 'transparent',
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: '0 18px 50px rgba(0,0,0,0.7)',
          }}
        >
          {/* Zone vidéo : on l'attrape pour DÉPLACER. Double-clic = play/pause. */}
          <div
            onPointerDown={onDragDown} onPointerMove={onDragMove} onPointerUp={onDragUp} onPointerCancel={onDragUp}
            onDoubleClick={togglePlay}
            className="absolute left-0 right-0 top-0"
            style={{ height: box.h - BAR, cursor: dragRef.current ? 'grabbing' : 'grab', touchAction: 'none' }}
          >
            {/* Titre de la vidéo (haut-gauche), lisible sur la vidéo */}
            <div className="absolute top-1.5 left-2.5 pointer-events-none" style={{ right: 118 }}>
              <div className="text-[11px] font-semibold text-white truncate" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                {S.vid?.title || ''}
              </div>
            </div>
            {/* Gros play/pause au centre (apparaît si en pause) */}
            {!pb.playing && (
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-black/55 flex items-center justify-center pointer-events-none">
                <Play size={26} className="ml-1 fill-white text-white" />
              </span>
            )}
            {/* Boutons haut-droite */}
            <div className="absolute top-1.5 right-1.5 flex gap-1.5">
              <button onClick={() => router.push('/app/video')} onPointerDown={e => e.stopPropagation()} title="Chercher une autre vidéo"
                className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition">
                <Search size={14} />
              </button>
              <button onClick={dockVideo} onPointerDown={e => e.stopPropagation()} title="Réduire en bas"
                className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition">
                <Minimize2 size={15} />
              </button>
              <button onClick={() => { if (S.vid) router.push(`/app/video/watch/${S.vid.videoId}`) }} onPointerDown={e => e.stopPropagation()} title="Rouvrir en grand"
                className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition">
                <Maximize2 size={15} />
              </button>
              <button onClick={closeVideoDock} onPointerDown={e => e.stopPropagation()} title="Fermer"
                className="w-8 h-8 rounded-full bg-black/60 hover:bg-red-600/80 text-white flex items-center justify-center transition">
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Barre de commandes (toujours visible) : −10 s · lecture · +10 s · progression · vitesse · son */}
          <div className="absolute bottom-0 inset-x-0 flex items-center gap-1.5 px-2"
            style={{ height: BAR, background: 'rgba(10,8,16,0.96)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <button onClick={() => seekTo(Math.max(0, pb.pos - 10))} title="Reculer de 10 s" aria-label="Reculer de 10 secondes"
              className="w-7 h-7 rounded-full text-white/85 hover:text-white hover:bg-white/10 flex items-center justify-center shrink-0 transition text-[9px] font-bold">−10</button>
            <button onClick={togglePlay} title={pb.playing ? 'Pause' : 'Lecture'}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-a1 to-a3 text-white flex items-center justify-center shrink-0 active:scale-90 transition">
              {pb.playing ? <Pause size={14} className="fill-white" /> : <Play size={14} className="ml-0.5 fill-white" />}
            </button>
            <button onClick={() => seekTo(Math.min(pb.dur || 0, pb.pos + 10))} title="Avancer de 10 s" aria-label="Avancer de 10 secondes"
              className="w-7 h-7 rounded-full text-white/85 hover:text-white hover:bg-white/10 flex items-center justify-center shrink-0 transition text-[9px] font-bold">+10</button>
            {box.w >= 360 && <span className="text-[10px] tabular-nums text-white/70 shrink-0">{fmt(pb.pos)}</span>}
            <input type="range" min={0} max={Math.max(1, Math.floor(pb.dur))} value={Math.floor(pb.pos)}
              onChange={e => seekTo(Number(e.target.value))} aria-label="Progression"
              className="flex-1 min-w-0" style={{ height: 4, appearance: 'none', borderRadius: 999, background: seekBg, outline: 'none', cursor: 'pointer' }} />
            {box.w >= 360 && <span className="text-[10px] tabular-nums text-white/70 shrink-0">{fmt(pb.dur)}</span>}
            <button onClick={cycleRate} title="Vitesse de lecture" aria-label="Vitesse de lecture"
              className={`h-7 px-1.5 rounded-full flex items-center justify-center shrink-0 transition text-[10px] font-bold ${rate !== 1 ? 'bg-a2/30 text-white' : 'text-white/80 hover:bg-white/10'}`}>{String(rate).replace('.', ',')}×</button>
            <button onClick={toggleMute} title={pb.muted ? 'Activer le son' : 'Couper le son'} aria-label={pb.muted ? 'Activer le son' : 'Couper le son'}
              className="w-7 h-7 rounded-full text-white/85 hover:text-white hover:bg-white/10 flex items-center justify-center shrink-0 transition" style={{ marginRight: 14 }}>
              {pb.muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
          </div>

          {/* Poignée de redimensionnement (coin bas-droit, bien visible) */}
          <div
            onPointerDown={onRzDown} onPointerMove={onRzMove} onPointerUp={onRzUp} onPointerCancel={onRzUp}
            title="Redimensionner"
            className="absolute"
            style={{
              right: 0, bottom: 0, width: 26, height: 26, zIndex: 2, cursor: 'nwse-resize', touchAction: 'none',
              background: 'linear-gradient(135deg, transparent 45%, rgba(255,255,255,0.55) 45%)',
            }}
          >
            <span style={{ position: 'absolute', right: 3, bottom: 3, width: 6, height: 6, borderRight: '2px solid #fff', borderBottom: '2px solid #fff' }} />
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
